import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import api from "../config/api";

interface ChangesContextType {
  refreshTrigger: number;
  triggerRefresh: () => void;
  /** Assignment changes still waiting on a decision, coder and supervisor sides summed. */
  pendingCount: number;
  pendingLoading: boolean;
}

const ChangesContext = createContext<ChangesContextType | undefined>(undefined);

/** How often the count is re-read while a page sits open. */
const POLL_INTERVAL_MS = 30000;

export const ChangesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [pendingLoading, setPendingLoading] = useState<boolean>(true);

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  /**
   * Counts the outstanding changes once, for everything that displays the figure.
   *
   * Held here rather than inside the hook that reads it because two places show it —
   * the nav badge and the dashboard card — and a hook counts again per caller: two
   * pollers, two pairs of requests every thirty seconds, and two numbers on screen
   * free to disagree about the same thing.
   */
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        // `limit=1` because only the counts are wanted: both endpoints return the
        // pending total alongside the page. This used to download both change lists in
        // full every 30 seconds to count them in the browser.
        //
        // Pending, not unread: a change that has been opened is not a change that has
        // been handled, so counting unread let the badge be cleared without the work
        // being done. It now reaches zero exactly when nothing is left to act on.
        const [coderResponse, supervisorResponse] = await Promise.all([
          api.get("/api/coder-changes?limit=1"),
          api.get("/api/supervisor-changes?limit=1"),
        ]);

        setPendingCount(
          (coderResponse.data.pending ?? 0) +
            (supervisorResponse.data.pending ?? 0),
        );
      } catch (error) {
        console.error("Error fetching pending changes count:", error);
        setPendingCount(0);
      } finally {
        setPendingLoading(false);
      }
    };

    fetchPendingCount();

    const interval = setInterval(fetchPendingCount, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refreshTrigger]);

  return (
    <ChangesContext.Provider
      value={{ refreshTrigger, triggerRefresh, pendingCount, pendingLoading }}
    >
      {children}
    </ChangesContext.Provider>
  );
};

export const useChangesContext = () => {
  const context = useContext(ChangesContext);
  if (!context) {
    throw new Error("useChangesContext must be used within ChangesProvider");
  }
  return context;
};
